/**
 * Audience-aware loader for the NSUK support widgets.
 *
 * The student and staff widgets are exposed by the same browser bundle. This
 * module owns that bundle's lifecycle so portal roots do not load assets or
 * initialise widgets more than once.
 */

const SCRIPT_ID = "student-support-widget-script";
const STYLE_ID = "student-support-widget-style";
const DEFAULT_ASSET_URL = "https://support-widget.nsuk.edu.ng";
const DEFAULT_BACKEND_URL = "https://support-api.nsuk.edu.ng";

let scriptPromise = null;
let activeWidget = null;
let activeKey = null;
let pendingKey = null;
let pendingPromise = null;
let lifecycleVersion = 0;

function trimTrailingSlashes(value) {
  return value.replace(/\/+$/, "");
}

function assetBaseUrl() {
  return trimTrailingSlashes(
    process.env.VUE_APP_SUPPORT_WIDGET_URL || DEFAULT_ASSET_URL
  );
}

function backendUrl() {
  return trimTrailingSlashes(
    process.env.VUE_APP_SUPPORT_API_URL || DEFAULT_BACKEND_URL
  );
}

/**
 * Resolve the support domain configured for a portal origin.
 *
 * @param {string} [hostname]
 * @returns {"test"|"pg"|"ug"|"nsuk"|"local"|null}
 */
export function deriveDomainId(hostname) {
  const host = (hostname === undefined
    ? window.location.hostname
    : hostname
  ).toLowerCase().replace(/\.$/, "");

  if (host === "localhost" || host === "127.0.0.1" || host === "::1") {
    return "local";
  }

  if (host === "nsuk.edu.ng" || host === "www.nsuk.edu.ng") {
    return "nsuk";
  }

  const match = /^(test|pg|ug)\.nsuk\.edu\.ng$/.exec(host);
  return match ? match[1] : null;
}

export function isEligibleHost(hostname) {
  return deriveDomainId(hostname) !== null;
}

function ensureStyle() {
  if (document.getElementById(STYLE_ID)) return;

  const link = document.createElement("link");
  link.id = STYLE_ID;
  link.rel = "stylesheet";
  link.href = `${assetBaseUrl()}/style.css`;
  document.head.appendChild(link);
}

function loadAssets() {
  ensureStyle();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const handleLoad = () => resolve();
    const handleError = () => reject(new Error("Failed to load the support widget bundle"));
    const existing = document.getElementById(SCRIPT_ID);

    if (existing) {
      if (window.StudentSupportWidget && window.StaffSupportWidget) {
        resolve();
        return;
      }
      existing.addEventListener("load", handleLoad, { once: true });
      existing.addEventListener("error", handleError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = `${assetBaseUrl()}/student-support-widget.iife.js`;
    script.async = true;
    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    document.body.appendChild(script);
  });

  return scriptPromise;
}

function widgetForAudience(audience) {
  return audience === "staff"
    ? window.StaffSupportWidget
    : window.StudentSupportWidget;
}

function configKey(audience, config) {
  return [
    audience,
    config.domainId,
    config.backendUrl,
    config.studentId || "",
    config.apiKey || "",
  ].join("|");
}

function destroy() {
  lifecycleVersion += 1;

  if (activeWidget && typeof activeWidget.destroy === "function") {
    activeWidget.destroy();
  }

  activeWidget = null;
  activeKey = null;
  pendingKey = null;
  pendingPromise = null;
}

async function initialize(audience, audienceConfig = {}) {
  const domainId = deriveDomainId();
  if (!domainId) return false;

  const config = {
    ...audienceConfig,
    domainId,
    backendUrl: backendUrl(),
  };
  const key = configKey(audience, config);

  if (activeKey === key) return true;
  if (pendingKey === key && pendingPromise) return pendingPromise;

  if (activeWidget || pendingPromise) destroy();

  const version = lifecycleVersion;
  let request;
  request = (async () => {
    try {
      await loadAssets();
      if (version !== lifecycleVersion) return false;

      const widget = widgetForAudience(audience);
      if (!widget || typeof widget.init !== "function") {
        throw new Error(`The ${audience} support widget is unavailable`);
      }

      widget.init(config);
      if (version !== lifecycleVersion) {
        if (typeof widget.destroy === "function") widget.destroy();
        return false;
      }

      activeWidget = widget;
      activeKey = key;
      return true;
    } catch (error) {
      // Support is best-effort and must never prevent a portal from loading.
      // eslint-disable-next-line no-console
      console.warn(`[SupportWidget] failed to initialise ${audience} support`, error);
      return false;
    } finally {
      if (pendingPromise === request) {
        pendingKey = null;
        pendingPromise = null;
      }
    }
  })();

  pendingKey = key;
  pendingPromise = request;
  return request;
}

const SupportWidget = {
  initGuest() {
    return initialize("guest");
  },

  initStudent({
    studentId,
    studentName,
    studentEmail,
    apiKey,
  } = {}) {
    if (!studentId || !apiKey) {
      // Never let a partial authenticated bootstrap silently become guest mode.
      // eslint-disable-next-line no-console
      console.warn("[SupportWidget] authenticated student bootstrap is incomplete");
      return Promise.resolve(false);
    }

    return initialize("student", {
      studentId,
      studentName,
      studentEmail,
      apiKey,
    });
  },

  initStaff() {
    return initialize("staff");
  },

  destroy,
  isEligibleHost,
};

export default SupportWidget;
